import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Receipt, Trash2 } from 'lucide-react';
import type { OneTimeCharge } from '@itr/shared';
import { formatMoney, formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, Drawer, EmptyState, SkeletonTable, useFetch, useToast } from './ui';

/**
 * One-time charges — support ticket fees and any other one-off fee with a
 * description. Pending until the client's next invoice picks them up.
 */

const toMinor = (value: string): number => Math.round(Number(value) * 100) || 0;
const fromMinor = (minor: number | null | undefined): string => (minor === null || minor === undefined ? '' : String(minor / 100));

interface TicketFeeInfo {
  existing: OneTimeCharge | null;
  price: { minor: number | null; source: 'client' | 'default' | 'none'; currency: string } | null;
  reason: string | null;
}

/* ================================================================== */
/* Client page card                                                   */
/* ================================================================== */

export function OneTimeChargesCard({ customerId }: { customerId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const canWrite = can('billing:write');
  const [adding, setAdding] = useState(false);
  const charges = useFetch(() => api.get<OneTimeCharge[]>('/api/billing/charges', { customerId, limit: 100 }), [customerId]);
  const prefs = useFetch(
    () => api.get<{ ticketFeeMinor: number | null; effective: { minor: number | null; source: string; currency: string } }>(
      `/api/customers/${customerId}/billing-prefs`,
    ),
    [customerId],
    { enabled: canWrite },
  );

  const rows = charges.data ?? [];
  const pending = rows.filter((c) => !c.invoiceId);
  const pendingTotal = pending.reduce((sum, c) => sum + c.totalMinor, 0);

  const remove = async (charge: OneTimeCharge) => {
    if (!confirm(`Remove "${charge.description}"?`)) return;
    try {
      await api.delete(`/api/billing/charges/${charge.id}`);
      charges.reload();
    } catch (err) {
      toast('Could not remove the charge', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Receipt size={14} />
        <span className="card-title">One-time charges</span>
        {pending.length > 0 && (
          <span className="card-subtitle">
            {pending.length} pending · {formatMoney(pendingTotal, pending[0]!.currency)} on the next invoice
          </span>
        )}
        <span className="grow" />
        {canWrite && (
          <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
            <Plus size={13} /> Add charge
          </button>
        )}
      </div>

      {charges.loading && !charges.data ? (
        <div style={{ padding: 16 }}>
          <SkeletonTable rows={2} cols={4} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<Receipt size={20} />} title="No one-time charges" text="Support ticket fees and other one-off fees appear here until they are invoiced." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th data-align="right" style={{ width: 70 }}>
                  Qty
                </th>
                <th data-align="right" style={{ width: 110 }}>
                  Total
                </th>
                <th style={{ width: 150 }}>Status</th>
                <th style={{ width: 110 }}>Added</th>
                {canWrite && <th style={{ width: 50 }} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((charge) => (
                <tr key={charge.id}>
                  <td>
                    {charge.description}
                    {charge.ticketId && charge.ticketReference && (
                      <Link to={`/tickets/${charge.ticketId}`} className="subtle mono" style={{ fontSize: 11.5, marginLeft: 6 }}>
                        {charge.ticketReference}
                      </Link>
                    )}
                  </td>
                  <td data-align="right" className="nums">
                    {charge.quantity}
                  </td>
                  <td data-align="right" className="nums" style={{ fontWeight: 600 }}>
                    {formatMoney(charge.totalMinor, charge.currency)}
                  </td>
                  <td>
                    {charge.invoiceId ? (
                      <Link to={`/invoices/${charge.invoiceId}`}>
                        <Badge intent="good">on {charge.invoiceNumber ?? 'invoice'}</Badge>
                      </Link>
                    ) : (
                      <Badge intent="warn">next invoice</Badge>
                    )}
                  </td>
                  <td className="subtle" style={{ fontSize: 12 }}>
                    {formatRelative(charge.createdAt)}
                  </td>
                  {canWrite && (
                    <td>
                      {!charge.invoiceId && (
                        <button className="btn btn-sm btn-icon" onClick={() => void remove(charge)} title="Remove" aria-label={`Remove ${charge.description}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && prefs.data && <ClientTicketFee customerId={customerId} prefs={prefs.data} onSaved={prefs.reload} />}

      <Drawer open={adding} onClose={() => setAdding(false)} title="Add a one-time charge">
        <ChargeForm
          customerId={customerId}
          onSaved={() => {
            setAdding(false);
            charges.reload();
          }}
        />
      </Drawer>
    </div>
  );
}

function ClientTicketFee({
  customerId,
  prefs,
  onSaved,
}: {
  customerId: string;
  prefs: { ticketFeeMinor: number | null; effective: { minor: number | null; source: string; currency: string } };
  onSaved: () => void;
}) {
  const toast = useToast();
  const [value, setValue] = useState(fromMinor(prefs.ticketFeeMinor));
  const [busy, setBusy] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/api/customers/${customerId}/billing-prefs`, { ticketFeeMinor: value.trim() === '' ? null : toMinor(value) });
      toast('Ticket fee saved', 'good');
      onSaved();
    } catch (err) {
      toast('Could not save', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const current = prefs.effective.minor === null ? 'none set' : formatMoney(prefs.effective.minor, prefs.effective.currency);
  return (
    <form onSubmit={save} className="card-body row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: 'var(--itr-border-w) solid var(--itr-border)', fontSize: 12.5 }}>
      <span>Support ticket fee for this client</span>
      <input
        className="input"
        type="number"
        step="0.01"
        min="0"
        style={{ width: 120 }}
        value={value}
        placeholder="default"
        onChange={(e) => setValue(e.target.value)}
        aria-label="Support ticket fee for this client"
      />
      <button className="btn btn-sm" type="submit" disabled={busy}>
        Save
      </button>
      <span className="subtle">
        Now: {current} ({prefs.effective.source === 'client' ? 'this client' : prefs.effective.source === 'default' ? 'default' : 'no default'}). Blank uses the
        default; 0 means no fee.
      </span>
    </form>
  );
}

function ChargeForm({
  customerId: fixedCustomerId,
  ticket,
  initialMinor,
  onSaved,
}: {
  /** Omitted when adding from the Invoices page: the form asks which client. */
  customerId?: string;
  /** Set for a support ticket fee. */
  ticket?: { id: string; reference: string; subject: string };
  initialMinor?: number | null;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [pickedCustomerId, setPickedCustomerId] = useState('');
  const customers = useFetch(() => api.get<Array<{ id: string; name: string }>>('/api/customers'), [], { enabled: !fixedCustomerId });
  const customerId = fixedCustomerId ?? pickedCustomerId;
  const [description, setDescription] = useState(ticket ? `Support ticket ${ticket.reference}: ${ticket.subject}` : '');
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState(fromMinor(initialMinor));
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/billing/charges', {
        customerId,
        kind: ticket ? 'ticket_fee' : 'other',
        ticketId: ticket?.id ?? null,
        description: description.trim(),
        quantity: Number(quantity) || 1,
        unitPriceMinor: toMinor(price),
      });
      toast(ticket ? 'Support fee added' : 'Charge added', 'good', 'It will be on the next invoice.');
      onSaved();
    } catch (err) {
      toast('Could not add the charge', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const total = (Number(quantity) || 0) * (Number(price) || 0);
  return (
    <form onSubmit={submit}>
      {!fixedCustomerId && (
        <div className="field">
          <label className="field-label">Client</label>
          <select className="select" required value={pickedCustomerId} onChange={(e) => setPickedCustomerId(e.target.value)}>
            <option value="">Select…</option>
            {(customers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="field">
        <label className="field-label">Description</label>
        <input className="input" required autoFocus={Boolean(fixedCustomerId)} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="On-site visit, hardware, setup…" />
        <span className="field-help">Printed on the invoice as the line text.</span>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ width: 110 }}>
          <label className="field-label">Quantity</label>
          <input className="input" type="number" step="0.01" min="0.01" required value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </div>
        <div className="field grow">
          <label className="field-label">Unit price</label>
          <input className="input" type="number" step="0.01" min="0" required value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
      </div>
      <p className="subtle" style={{ fontSize: 12.5, margin: '0 0 14px' }}>
        Total {total.toFixed(2)} · added to this client's next invoice.
      </p>
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Adding…' : ticket ? 'Add support fee' : 'Add charge'}
      </button>
    </form>
  );
}

/* ================================================================== */
/* Ticket page                                                        */
/* ================================================================== */

/** "Add support fee" on a ticket — or, once added, what it is and whether it has been invoiced. */
export function TicketSupportFee({ ticket }: { ticket: { id: string; reference: string; subject: string; customerId: string | null } }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const enabled = can('billing:write') && Boolean(ticket.customerId);
  const info = useFetch(() => api.get<TicketFeeInfo>('/api/billing/ticket-fee', { ticketId: ticket.id }), [ticket.id], { enabled });
  if (!enabled || !info.data || info.data.reason) return null;

  const existing = info.data.existing;
  if (existing) {
    return (
      <Link className="btn btn-sm" to={`/clients/${ticket.customerId}#billing-section`} title="Support fee for this ticket">
        <Receipt size={13} /> Fee {formatMoney(existing.totalMinor, existing.currency)} · {existing.invoiceId ? `on ${existing.invoiceNumber ?? 'invoice'}` : 'pending'}
      </Link>
    );
  }

  return (
    <>
      <button className="btn btn-sm" onClick={() => setOpen(true)}>
        <Receipt size={13} /> Add support fee
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title={`Support fee — ${ticket.reference}`}>
        <ChargeForm
          customerId={ticket.customerId!}
          ticket={ticket}
          initialMinor={info.data.price?.minor ?? null}
          onSaved={() => {
            setOpen(false);
            info.reload();
          }}
        />
      </Drawer>
    </>
  );
}

/* ================================================================== */
/* Invoices page tab                                                  */
/* ================================================================== */

/** Default ticket fee, plus every client's pending charges in one place. */
export function ChargesTab({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const settings = useFetch(() => api.get<{ defaultTicketFeeMinor: number | null }>('/api/billing/settings'), []);
  const pending = useFetch(() => api.get<Array<OneTimeCharge>>('/api/billing/charges', { state: 'pending', limit: 500 }), []);
  const [fee, setFee] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const shownFee = fee ?? fromMinor(settings.data?.defaultTicketFeeMinor);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put('/api/billing/settings', { defaultTicketFeeMinor: shownFee.trim() === '' ? null : toMinor(shownFee) });
      toast('Default ticket fee saved', 'good');
      setFee(null);
      settings.reload();
    } catch (err) {
      toast('Could not save', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const rows = pending.data ?? [];
  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <form onSubmit={save} className="card-body row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <strong>Default support ticket fee</strong>
          <input
            className="input"
            type="number"
            step="0.01"
            min="0"
            style={{ width: 130 }}
            value={shownFee}
            placeholder="none"
            disabled={!canWrite}
            onChange={(e) => setFee(e.target.value)}
            aria-label="Default support ticket fee"
          />
          {canWrite && (
            <button className="btn btn-sm btn-primary" type="submit" disabled={busy}>
              Save
            </button>
          )}
          <span className="subtle" style={{ fontSize: 12 }}>
            Prefills "Add support fee" on tickets, in the client's account currency. A client can override it on its own page.
          </span>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <Receipt size={14} />
          <span className="card-title">Pending one-time charges</span>
          <span className="card-subtitle">added to each client's next invoice</span>
          <span className="grow" />
          {canWrite && (
            <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
              <Plus size={13} /> Add charge
            </button>
          )}
        </div>
        {pending.loading && !pending.data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={3} cols={4} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<Receipt size={20} />} title="Nothing pending" text="Add a charge here or on a client's page, or a support fee from a ticket." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Description</th>
                  <th data-align="right" style={{ width: 110 }}>
                    Total
                  </th>
                  <th style={{ width: 110 }}>Added</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((charge) => (
                  <tr key={charge.id}>
                    <td>
                      <Link to={`/clients/${charge.customerId}#billing-section`}>{charge.customerName ?? charge.customerId}</Link>
                    </td>
                    <td>
                      {charge.description}
                      {charge.ticketId && charge.ticketReference && (
                        <Link to={`/tickets/${charge.ticketId}`} className="subtle mono" style={{ fontSize: 11.5, marginLeft: 6 }}>
                          {charge.ticketReference}
                        </Link>
                      )}
                    </td>
                    <td data-align="right" className="nums" style={{ fontWeight: 600 }}>
                      {formatMoney(charge.totalMinor, charge.currency)}
                    </td>
                    <td className="subtle" style={{ fontSize: 12 }}>
                      {formatRelative(charge.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={adding} onClose={() => setAdding(false)} title="Add a one-time charge">
        <ChargeForm
          onSaved={() => {
            setAdding(false);
            pending.reload();
          }}
        />
      </Drawer>
    </>
  );
}
